import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import Layout from '@/components/Layout'
import { useAuth } from '@/contexts/AuthContext'
import CartDrawer from '@/components/cart/CartDrawer'

// Lazy load all components for code splitting
const Home = lazy(() => import('@/components/Home'))
const About = lazy(() => import('@/components/About'))
const Services = lazy(() => import('@/components/Services'))
const Meditation = lazy(() => import('@/components/Meditation'))
const Yoga = lazy(() => import('@/components/Yoga'))
const Chakras = lazy(() => import('@/components/Chakras'))
const Ayurveda = lazy(() => import('@/components/Ayurveda'))
const Team = lazy(() => import('@/components/Team'))
const Contact = lazy(() => import('@/components/Contact'))
const Programs = lazy(() => import('@/components/Programs'))
const CoursePlayer = lazy(() => import('@/components/CoursePlayer'))
const Resources = lazy(() => import('@/components/Resources'))
const FranchisePortal = lazy(() => import('@/components/FranchisePortal'))
const FranchiseOnboarding = lazy(() => import('@/components/FranchiseOnboarding'))
const StudioDashboard = lazy(() => import('@/components/StudioDashboard'))
const MemberDashboard = lazy(() => import('@/components/MemberDashboard'))
const CulturalEvents = lazy(() => import('@/components/CulturalEvents'))
const RoyalMagazine = lazy(() => import('@/components/RoyalMagazine'))
const MemberBenefits = lazy(() => import('@/components/MemberBenefits'))
const AIMarketingTools = lazy(() => import('@/components/AIMarketingTools'))
const Gurukul = lazy(() => import('@/components/Gurukul'))
const SattvaLifeCoaching = lazy(() => import('@/components/SattvaLifeCoaching'))
const OpportunitiesOverview = lazy(() => import('@/components/OpportunitiesOverview'))
const MembershipOverview = lazy(() => import('@/components/MembershipOverview'))
const MembershipGettingStarted = lazy(() => import('@/components/MembershipGettingStarted'))
const CommunityHub = lazy(() => import('@/components/CommunityHub'))
const YogaCertification = lazy(() => import('@/components/YogaCertification'))
const AgeReversalYoga = lazy(() => import('@/components/AgeReversalYoga'))
const YogaLogin = lazy(() => import('@/components/YogaLogin'))
const YogaRegister = lazy(() => import('@/components/YogaRegister'))
const Messages = lazy(() => import('@/components/Messages'))
const ClubOfRoyalsSummer = lazy(() => import('@/components/ClubOfRoyalsSummer'))
const HowItWorks = lazy(() => import('@/components/HowItWorks'))
const WorkflowDemo = lazy(() => import('@/components/WorkflowDemo'))
const Onboarding = lazy(() => import('@/components/Onboarding'))
const PersonaBenefits = lazy(() => import('@/components/PersonaBenefits'))
const SalesPitch = lazy(() => import('@/components/SalesPitch'))
const AyurvedaDietary = lazy(() => import('@/components/AyurvedaDietary'))
const AstrologyAyurveda = lazy(() => import('@/components/AstrologyAyurveda'))
const ThirtyMinutesYoga = lazy(() => import('@/components/ThirtyMinutesYoga'))
const YogaLayout = lazy(() => import('@/components/YogaLayout'))
const AryanirogLanding = lazy(() => import('@/components/AryanirogLanding'))
const GitaCourseViewer = lazy(() => import('@/components/GitaCourseViewer'))
const BhagavatamCourseViewer = lazy(() => import('@/components/BhagavatamCourseViewer'))
const AryanirogLayout = lazy(() => import('@/components/AryanirogLayout'))
const PrivacyPolicy = lazy(() => import('@/components/legal/PrivacyPolicy'))
const TermsOfService = lazy(() => import('@/components/legal/TermsOfService'))
const CookiePolicy = lazy(() => import('@/components/legal/CookiePolicy'))
const Accessibility = lazy(() => import('@/components/legal/Accessibility'))
const TeacherDashboard = lazy(() => import('@/components/TeacherDashboard'))
const POSWindow        = lazy(() => import('@/components/POSWindow'))
const StudioAdminDashboard = lazy(() => import('@/components/StudioAdminDashboard'))
const PartnerDashboard = lazy(() => import('@/components/PartnerDashboard'))
const FindPractitioner = lazy(() => import('@/components/FindPractitioner'))
const Marketplace = lazy(() => import('@/components/Marketplace'))
const OnboardingWizard = lazy(() => import('@/components/OnboardingWizard'))
const NotFound = lazy(() => import('@/components/NotFound'))
const Store = lazy(() => import('@/components/Store'))
const Checkout = lazy(() => import('@/components/Checkout'))
const YogaSecurity = lazy(() => import('@/components/YogaSecurity'))

// Generic auth guard — unauthenticated → /login; optional role check (single or array)
function ProtectedRoute({ children, requiredRole }: { children: React.ReactNode; requiredRole?: string | string[] }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (requiredRole) {
    const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole]
    if (!allowed.includes(user.role)) return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

// /studio/dashboard — accessible to all non-viewer roles; viewer/guest → member dashboard
function StudioDashboardRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return null
  if (!user || user.role === 'viewer') return <Navigate to="/member/dashboard" replace />
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <StudioDashboard />
    </motion.div>
  )
}

// /partner/dashboard — franchise_owner only; unauthenticated → /login
function PartnerRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return null
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'franchise_owner') return <Navigate to="/dashboard" replace />
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <PartnerDashboard />
    </motion.div>
  )
}

// /dashboard generic alias — redirect to persona-specific home
function ProtectedStudioRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return null
  if (!user || user.role === 'viewer') return <Navigate to="/member/dashboard" replace />
  if (user.role === 'counselor' || user.role === 'instructor') return <Navigate to="/teacher/dashboard" replace />
  if (user.role === 'studio_admin') return <Navigate to="/studio/admin/dashboard" replace />
  if (user.role === 'franchise_owner') return <Navigate to="/partner/dashboard" replace />
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <StudioDashboard />
    </motion.div>
  )
}

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sage-50 to-white">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <div className="w-16 h-16 border-4 border-sage-200 border-t-sage-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-sage-600 font-medium">Loading...</p>
    </motion.div>
  </div>
)

function App() {
  const location = useLocation()
  const isYogaBrand = location.pathname.startsWith('/30minutes-yoga')
  const isAryanirog = location.pathname.startsWith('/aryanirog')
  const isPOSWindow  = location.pathname === '/teacher/pos'

  if (isPOSWindow) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/teacher/pos" element={<POSWindow />} />
        </Routes>
      </Suspense>
    )
  }

  if (isAryanirog) {
    return (
      <div className="min-h-screen">
        <CartDrawer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/aryanirog" element={<AryanirogLayout />}>
              <Route index element={<AryanirogLanding />} />
              <Route path="ayurveda" element={<Ayurveda />} />
              <Route path="dietary-guide" element={<AyurvedaDietary />} />
              <Route path="astrology" element={<AstrologyAyurveda />} />
              <Route path="chakras" element={<Chakras />} />
              <Route path="yoga" element={<Yoga />} />
              <Route path="meditation" element={<Meditation />} />
              <Route path="programs" element={<Programs />} />
              <Route path="resources" element={<Resources />} />
              <Route path="certification" element={<YogaCertification />} />
              <Route path="community" element={<CommunityHub />} />
              <Route path="events" element={<CulturalEvents />} />
              <Route path="benefits" element={<MemberBenefits />} />
              <Route path="magazine" element={<RoyalMagazine />} />
              <Route path="summer" element={<ClubOfRoyalsSummer />} />
              <Route path="franchise" element={<FranchisePortal />} />
              <Route path="franchise/onboarding" element={<FranchiseOnboarding />} />
              <Route path="contact" element={<Contact />} />
              <Route path="team" element={<Team partnerSlug="aryanirog" />} />
              <Route path="about" element={<About />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
    )
  }

  if (isYogaBrand) {
    return (
      <div className="min-h-screen">
        <CartDrawer />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/30minutes-yoga" element={<YogaLayout />}>
              <Route index element={<ThirtyMinutesYoga />} />
              <Route path="yoga" element={<Yoga />} />
              <Route path="meditation" element={<Meditation />} />
              <Route path="chakras" element={<Chakras />} />
              <Route path="ayurveda" element={<Ayurveda />} />
              <Route path="programs" element={<Programs />} />
              <Route path="resources" element={<Resources />} />
              <Route path="certification" element={<YogaCertification />} />
              <Route path="community" element={<CommunityHub />} />
              <Route path="events" element={<CulturalEvents />} />
              <Route path="benefits" element={<MemberBenefits />} />
              <Route path="magazine" element={<RoyalMagazine />} />
              <Route path="summer" element={<ClubOfRoyalsSummer />} />
              <Route path="franchise" element={<FranchisePortal />} />
              <Route path="franchise/onboarding" element={<FranchiseOnboarding />} />
              <Route path="contact" element={<Contact />} />
              <Route path="team" element={<Team partnerSlug="30minutes-yoga" />} />
              <Route path="about" element={<About />} />
            </Route>
          </Routes>
        </Suspense>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <CartDrawer />
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route
            path="/"
            element={
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Home />
              </motion.div>
            }
          />
          <Route
            path="/gurukul"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Gurukul />
              </motion.div>
            }
          />
          <Route
            path="/life-coaching"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <SattvaLifeCoaching />
              </motion.div>
            }
          />
          <Route
            path="/about"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <About />
              </motion.div>
            }
          />
          <Route
            path="/services"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Services />
              </motion.div>
            }
          />
          <Route
            path="/meditation"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Meditation />
              </motion.div>
            }
          />
          <Route
            path="/yoga"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Yoga />
              </motion.div>
            }
          />
          <Route
            path="/chakras"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Chakras />
              </motion.div>
            }
          />
          <Route
            path="/ayurveda"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Ayurveda />
              </motion.div>
            }
          />
          <Route
            path="/team"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Team />
              </motion.div>
            }
          />
          <Route
            path="/programs"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Programs />
              </motion.div>
            }
          />
          <Route
            path="/courses/srimad-bhagavatam"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <BhagavatamCourseViewer />
              </motion.div>
            }
          />
          <Route
            path="/courses/bhagavad-gita"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <GitaCourseViewer />
              </motion.div>
            }
          />
          <Route
            path="/courses/:slug"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CoursePlayer />
              </motion.div>
            }
          />
          <Route
            path="/resources"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Resources />
              </motion.div>
            }
          />
          <Route
            path="/contact"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Contact />
              </motion.div>
            }
          />
          <Route
            path="/franchise"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <FranchisePortal />
              </motion.div>
            }
          />
          <Route
            path="/franchise/onboarding"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <FranchiseOnboarding />
              </motion.div>
            }
          />
          <Route
            path="/studio/dashboard"
            element={<StudioDashboardRoute />}
          />
          <Route
            path="/member/dashboard"
            element={
              <ProtectedRoute>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <MemberDashboard />
                </motion.div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/teacher/dashboard"
            element={
              <ProtectedRoute requiredRole={["instructor", "counselor"]}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <TeacherDashboard />
                </motion.div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/studio/admin/dashboard"
            element={
              <ProtectedRoute requiredRole="studio_admin">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <StudioAdminDashboard />
                </motion.div>
              </ProtectedRoute>
            }
          />
          <Route path="/partner/dashboard" element={<PartnerRoute />} />
          <Route
            path="/find-practitioner"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <FindPractitioner />
              </motion.div>
            }
          />
          <Route
            path="/cultural-events"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CulturalEvents />
              </motion.div>
            }
          />
          <Route
            path="/royal-magazine"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <RoyalMagazine />
              </motion.div>
            }
          />
          <Route
            path="/member-benefits"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <MemberBenefits />
              </motion.div>
            }
          />
          <Route
            path="/ai-marketing"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AIMarketingTools />
              </motion.div>
            }
          />
          <Route
            path="/dashboard/agents/marketplace"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AIMarketingTools />
              </motion.div>
            }
          />
          <Route
            path="/community"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CommunityHub />
              </motion.div>
            }
          />
          <Route
            path="/summer-hub"
            element={
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <ClubOfRoyalsSummer />
              </motion.div>
            }
          />
          <Route
            path="/gurukul/certification"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <YogaCertification />
              </motion.div>
            }
          />
          <Route
            path="/gurukul/age-reversal"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AgeReversalYoga />
              </motion.div>
            }
          />
          {/* /dashboard alias → role-aware dashboard */}
          <Route path="/dashboard" element={<ProtectedStudioRoute />} />
          {/* Two-factor setup + own sign-in history */}
          <Route path="/security" element={<ProtectedRoute><YogaSecurity /></ProtectedRoute>} />
          {/* Messages / enrollment notifications */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Messages />
                </motion.div>
              </ProtectedRoute>
            }
          />
          {/* About section — AI platform explainer pages (legacy paths) */}
          <Route path="/about/how-it-works" element={<Navigate to="/how-it-works" replace />} />
          <Route
            path="/about/workflow-demo"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <WorkflowDemo />
              </motion.div>
            }
          />
          <Route
            path="/about/onboarding"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Onboarding />
              </motion.div>
            }
          />
          {/* How It Works — top-level section */}
          <Route
            path="/how-it-works"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <HowItWorks />
              </motion.div>
            }
          />
          <Route
            path="/how-it-works/personas"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <PersonaBenefits />
              </motion.div>
            }
          />
          <Route
            path="/opportunities/overview"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <OpportunitiesOverview />
              </motion.div>
            }
          />
          <Route
            path="/membership/overview"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <MembershipOverview />
              </motion.div>
            }
          />
          <Route
            path="/membership/getting-started"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <MembershipGettingStarted />
              </motion.div>
            }
          />
          <Route path="/how-it-works/demo" element={<Navigate to="/about/workflow-demo" replace />} />
          <Route path="/how-it-works/start" element={<Navigate to="/about/onboarding" replace />} />
          <Route
            path="/why-club-of-royals"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <SalesPitch />
              </motion.div>
            }
          />
          <Route
            path="/ayurveda/dietary-guide"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AyurvedaDietary />
              </motion.div>
            }
          />
          <Route
            path="/ayurveda/astrology"
            element={
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <AstrologyAyurveda />
              </motion.div>
            }
          />
          {/* COR Onboarding Wizards — /onboarding and /onboarding/:persona */}
          <Route
            path="/onboarding"
            element={
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                <OnboardingWizard />
              </motion.div>
            }
          />
          <Route
            path="/onboarding/:persona"
            element={
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                <OnboardingWizard />
              </motion.div>
            }
          />
          {/* COR Marketplace — Practitioner ↔ Studio discovery */}
          <Route
            path="/marketplace"
            element={
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Marketplace />
              </motion.div>
            }
          />
          {/* Store & Checkout */}
          <Route path="/store" element={<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><Store /></motion.div>} />
          <Route path="/checkout" element={<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}><Checkout /></motion.div>} />
          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/accessibility" element={<Accessibility />} />

          {/* Auth pages — rendered without the main Layout */}
          <Route path="/login" element={<YogaLogin />} />
          <Route path="/register" element={<YogaRegister />} />
          {/* 404 catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Layout>
    </div>
  )
}

export default App